import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BarChart3, Home, LockKeyhole, Plane, TrendingUp, Trophy, Users } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { positionLabel, TEAM_NAME } from "@/lib/dvStats";
import LangToggle from "@/components/datavolley/LangToggle";
import SeasonView from "@/components/datavolley/SeasonView";
import StatsView from "@/components/datavolley/StatsView";


const setsLine = (m) =>
  [1, 2, 3, 4, 5]
    .filter((n) => m.set_scores?.[String(n)])
    .map((n) => `${m.set_scores[String(n)][0]}-${m.set_scores[String(n)][1]}`)
    .join(" · ");

const resultOf = (m) => {
  let us = 0;
  let them = 0;
  Object.values(m.set_scores || {}).forEach(([a, b]) => {
    if (a > b) us += 1;
    else if (b > a) them += 1;
  });
  return us + them ? `${us} - ${them}` : null;
};

export default function PublicView({ onLogin }) {
  const { t } = useT();
  const [view, setView] = useState("matches");
  const [match, setMatch] = useState(null);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="dv-public-view">
      <header className="sticky top-0 z-40 bg-slate-950 text-white">
        <div className="px-3 sm:px-5 h-12 sm:h-14 flex items-center gap-2 sm:gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="dv-home-link">
            <img src="/club-logo-white.png" alt="Ν.Α.Ο. Σούδας" className="h-10 w-auto" />
          </Link>
          <span className="font-heading font-black text-lg tracking-tight hidden sm:inline">
            Data<span className="text-sunset">Volley</span>
          </span>
          <nav className="ml-2 flex items-center gap-1 rounded-xl bg-slate-800/70 p-1">
            {match ? (
              <button onClick={() => setMatch(null)} data-testid="dv-public-back-btn" className="dv-btn h-9 px-3 rounded-lg text-sm font-semibold text-slate-300 hover:text-white flex items-center gap-1.5">
                <ArrowLeft className="h-4 w-4" /> {t("dv_back")}
              </button>
            ) : (
              <>
                <Pill active={view === "matches"} onClick={() => setView("matches")} icon={Trophy} label={t("dv_matches")} testid="dv-public-nav-matches" />
                <Pill active={view === "players"} onClick={() => setView("players")} icon={Users} label={t("dv_players")} testid="dv-public-nav-players" />
                <Pill active={view === "season"} onClick={() => setView("season")} icon={TrendingUp} label={t("dv_season")} testid="dv-public-nav-season" />
              </>
            )}
          </nav>
          <LangToggle className="ml-auto" />
          <button
            onClick={onLogin}
            data-testid="dv-public-login-btn"
            className="dv-btn h-9 px-3 rounded-lg text-sm font-semibold bg-sunset text-white flex items-center gap-1.5"
          >
            <LockKeyhole className="h-4 w-4" /> <span className="hidden sm:inline">{t("dv_coach_login")}</span>
          </button>
        </div>
      </header>

      <main className="max-w-[1500px] mx-auto px-3 sm:px-5 py-4">
        {match ? (
          <PublicMatchStats matchId={match.id} />
        ) : (
          <>
            {view === "matches" && <PublicMatches onOpen={setMatch} />}
            {view === "players" && <PublicPlayers />}
            {view === "season" && <SeasonView endpoint="/datavolley/public/season" client={apiClient} />}
          </>
        )}
      </main>
    </div>
  );
}

function Pill({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`dv-btn h-9 px-3 rounded-lg text-sm font-semibold flex items-center gap-1.5 ${active ? "bg-white text-slate-900" : "text-slate-300 hover:text-white"}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );
}

function PublicMatches({ onOpen }) {
  const { t } = useT();
  const [matches, setMatches] = useState(null);
  useEffect(() => {
    apiClient.get("/datavolley/public/matches").then((r) => setMatches(r.data)).catch(() => toast.error("Failed to load"));
  }, []);
  if (!matches) return <p className="text-center text-slate-500 py-10">…</p>;
  if (matches.length === 0) return <p className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">{t("dv_no_matches")}</p>;
  return (
    <div className="flex flex-col gap-4" data-testid="dv-public-matches">
      <h2 className="font-heading font-black text-xl text-slate-900">{t("dv_matches")}</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {matches.map((m) => {
          const res = resultOf(m);
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => onOpen(m)}
              data-testid={`dv-public-match-${m.id}`}
              className="dv-btn text-left rounded-2xl bg-white border border-slate-200 shadow-soft p-4 flex items-center justify-between gap-3 hover:border-ocean"
            >
              <div className="min-w-0">
                <p className="font-heading font-black text-xl text-slate-900 leading-tight">{TEAM_NAME} <span className="text-slate-400 text-sm font-bold">vs</span></p>
                <p className="font-heading font-black text-xl text-slate-900 truncate">{m.opponent}</p>
                <p className="text-sm text-slate-500 flex items-center gap-2 mt-0.5">
                  {m.date}
                  <span className="inline-flex items-center gap-1">
                    {m.venue === "home" ? <Home className="h-3.5 w-3.5" /> : <Plane className="h-3.5 w-3.5" />}
                    {m.venue === "home" ? t("dv_home") : t("dv_away")}
                  </span>
                  {m.competition && <span className="inline-flex items-center gap-1"><Trophy className="h-3 w-3" /> {m.competition}</span>}
                </p>
              </div>
              <div className="shrink-0 text-right">
                <p className="font-heading font-black text-3xl text-slate-900 leading-none" data-testid={`dv-public-result-${m.id}`}>{res || "–"}</p>
                {res && <p className="text-[11px] text-slate-500 mt-1 whitespace-nowrap">{setsLine(m)}</p>}
                <p className="text-[11px] text-slate-500 flex items-center justify-end gap-1 mt-1"><BarChart3 className="h-3 w-3" /> {t("dv_stats")}</p>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PublicPlayers() {
  const { t, lang } = useT();
  const [players, setPlayers] = useState(null);
  useEffect(() => {
    apiClient.get("/datavolley/public/players").then((r) => setPlayers(r.data)).catch(() => toast.error("Failed to load"));
  }, []);
  if (!players) return <p className="text-center text-slate-500 py-10">…</p>;
  return (
    <div className="flex flex-col gap-4" data-testid="dv-public-players">
      <h2 className="font-heading font-black text-xl text-slate-900">{t("dv_roster")}</h2>
      {players.length === 0 ? (
        <p className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">{t("dv_no_players")}</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {players.map((p) => (
            <div key={`${p.number}-${p.name}`} className="rounded-2xl bg-white border border-slate-200 shadow-soft p-4 flex items-center gap-3" data-testid={`dv-public-player-${p.number}`}>
              <span className="h-12 w-12 rounded-xl bg-slate-900 text-white font-heading font-black text-xl flex items-center justify-center shrink-0">{p.number}</span>
              <div className="min-w-0">
                <p className="font-bold text-slate-900 leading-tight line-clamp-2">{p.name}</p>
                <p className="text-xs text-slate-500">{positionLabel(p.position, lang)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PublicMatchStats({ matchId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiClient.get(`/datavolley/public/matches/${matchId}`).then((r) => setData(r.data)).catch(() => toast.error("Failed to load"));
  }, [matchId]);
  if (!data) return <p className="text-center text-slate-500 py-10">…</p>;
  return <StatsView match={data.match} events={data.events} readOnly />;
}
